class Questionnaire {
    constructor(wrapper) {
        this.wrapper = wrapper;
        this.initialize();
    }

    initialize() {
        // Mark every field already in the DOM at load — a flat-seeded group's
        // real children are only ever among these. A question added later in
        // this editing session (Add Question/Import) must never be swept into
        // a preceding group just because it landed right after it positionally.
        this.wrapper.querySelectorAll(':scope > .nested-fields').forEach((field) => {
            field.dataset.presentAtLoad = 'true';
        });

        this.evaluateNewPositions();
        this.bindEvents();
    }

    bindEvents() {
        // Handle up/down arrows
        this.wrapper.addEventListener('click', (e) => {
            const upArrow = e.target.closest('.present-up-arrow');
            const downArrow = e.target.closest('.present-down-arrow');
            if (!upArrow && !downArrow) return;

            e.preventDefault();
            if ((upArrow || downArrow).classList.contains('arrow-locked')) return;

            if (upArrow) {
                this.moveRecord(upArrow, -1);
            } else {
                this.moveRecord(downArrow, 1);
            }
        });

        // Handle remove field button
        this.wrapper.addEventListener('click', (e) => {
            if (e.target.closest('.remove-icon')) {
                setTimeout(() => {
                    this.evaluateNewPositions();
                }, 500);
            }
        });

        // Watch for DOM changes to detect new questions being added
        this.observeDOMChanges();
    }

    evaluateNewPositions() {
        // Check if this is a grouped question container (for sub-questions)
        const isGroupedContainer = this.wrapper.id && this.wrapper.id.includes('grouped-question-container');

        const visibleNestedFields = Array.from(this.wrapper.querySelectorAll('.nested-fields')).filter(el => {
            // Only include nested fields that have arrow controls (questions, not options)
            // If this is a grouped container, include all sub-questions
            // If this is the main container, exclude sub-questions
            return el.offsetParent !== null &&
                el.querySelector('.present-up-arrow, .present-down-arrow') &&
                (isGroupedContainer || !this.isSubQuestionInGroupedQuestion(el));
        });

        visibleNestedFields.forEach((nestedField, index) => {
            const pos = nestedField.querySelector('.child-position');
            if (pos) {
                pos.value = index + 1;
                pos.className = `child-position recordPosition-${index + 1}`;
            }
        });

        if (!isGroupedContainer) {
            this.lockFlatSeededGroupArrows(visibleNestedFields);
        }
    }

    // A seeded default group (e.g. "Drivers License/ID Information") has no real
    // sub_questions — its children are flat top-level siblings that only look
    // grouped because they're positioned right after the header. The up/down
    // arrows have no concept of that grouping, so moving one enough times walks
    // it straight through other groups and silently reassigns it to whichever
    // group it lands next to (issue #25). Lock the arrows on the header and its
    // flat children entirely rather than trying to make the move "group-aware".
    lockFlatSeededGroupArrows(topLevelFields) {
        const questionType = (field) => {
            const input = field.querySelector('input[type="hidden"][name$="[question_type]"]');
            return input ? input.value : null;
        };
        const hasRealSubQuestions = (field) => {
            const container = field.querySelector('[id^="grouped-question-container"]');
            return !!(container && container.querySelector('.nested-fields'));
        };

        const locked = new Set();
        topLevelFields.forEach((field, index) => {
            // Only a group present at load can be a seeded default worth
            // protecting — a group an admin just added this session is still
            // being built and has no "original flat children" to preserve.
            if (questionType(field) !== 'question_group' || hasRealSubQuestions(field) || !field.dataset.presentAtLoad) return;

            locked.add(field);
            for (let i = index + 1; i < topLevelFields.length; i++) {
                const sibling = topLevelFields[i];
                // A group header ends the run; a field added after load (Add
                // Question/Import) was never part of this group's original
                // children, however it happens to land positionally.
                if (questionType(sibling) === 'question_group' || !sibling.dataset.presentAtLoad) break;
                locked.add(sibling);
            }
        });

        topLevelFields.forEach((field) => {
            const arrows = field.querySelectorAll('.present-up-arrow, .present-down-arrow');
            arrows.forEach((arrow) => {
                arrow.classList.toggle('arrow-locked', locked.has(field));
                arrow.title = locked.has(field)
                    ? "Part of a default question group — its order can't be changed here"
                    : '';
            });
        });
    }

    moveRecord(arrowElement, direction) {
        const parent = this.wrapper;
        // Step 1: Build an array of all main question blocks
        const allNodes = Array.from(parent.children);
        const blocks = [];
        let i = 0;

        // Check if this is a grouped question container (for sub-questions)
        const isGroupedContainer = parent.id && parent.id.includes('grouped-question-container');

        while (i < allNodes.length) {
            const node = allNodes[i];
            if (
                node.classList.contains('nested-fields') &&
                node.offsetParent !== null &&
                node.querySelector('.present-up-arrow, .present-down-arrow')
            ) {
                // If this is a grouped container, include all sub-questions
                // If this is the main container, exclude sub-questions
                if (isGroupedContainer || !this.isSubQuestionInGroupedQuestion(node)) {
                    // For grouped containers, each sub-question is a single block
                    // For main container, grouped questions are multi-node blocks
                    if (isGroupedContainer) {
                        blocks.push([node]);
                        i++;
                    } else {
                        // Is this a grouped question in the main container?
                        if (node.querySelector('#grouped-question-container')) {
                            // Collect this node and all its siblings until the next .nested-fields or end
                            const block = [node];
                            let j = i + 1;
                            while (
                                j < allNodes.length &&
                                !allNodes[j].classList.contains('nested-fields')
                                ) {
                                block.push(allNodes[j]);
                                j++;
                            }
                            blocks.push(block);
                            i = j;
                        } else {
                            blocks.push([node]);
                            i++;
                        }
                    }
                } else {
                    i++;
                }
            } else {
                i++;
            }
        }

        // Step 2: Find the block containing the arrowElement
        const parentRow = arrowElement.closest('.nested-fields');
        const blockIndex = blocks.findIndex(block => block.includes(parentRow));
        if (blockIndex === -1) return;

        const newIndex = blockIndex + direction;
        if (newIndex < 0 || newIndex >= blocks.length) return;

        // Don't swap into a locked group's territory — an unlocked question
        // landing next to a group header would then read as one of its flat
        // children on reload (same underlying fragility this whole fix targets).
        const targetIsLocked = blocks[newIndex].some((node) => {
            const arrow = node.querySelector && node.querySelector('.present-up-arrow, .present-down-arrow');
            return arrow && arrow.classList.contains('arrow-locked');
        });
        if (targetIsLocked) return;

        // Step 3: Swap the blocks
        const temp = blocks[blockIndex];
        blocks[blockIndex] = blocks[newIndex];
        blocks[newIndex] = temp;

        // Step 4: Re-append all blocks in new order
        blocks.forEach(block => {
            block.forEach(node => parent.appendChild(node));
        });

        // Step 5: Re-evaluate positions
        this.evaluateNewPositions();
    }

    setNewQuestionPosition() {
        // Find all question containers with arrow controls
        const questionContainers = Array.from(this.wrapper.querySelectorAll('.nested-fields')).filter(el => {
            return el.offsetParent !== null &&
                el.querySelector('.present-up-arrow, .present-down-arrow') &&
                !this.isSubQuestionInGroupedQuestion(el);
        });

        // Simply reassign positions sequentially starting from 1
        questionContainers.forEach((container, index) => {
            const posField = container.querySelector('.child-position');
            if (posField) {
                posField.value = index + 1;
            }
        });
    }

    setNewOptionPosition() {
        // Find all option containers within this wrapper
        const optionContainers = Array.from(this.wrapper.querySelectorAll('.nested-fields')).filter(el => {
            return el.offsetParent !== null && el.querySelector('input[name*="[value]"]') && !el.querySelector('.present-up-arrow, .present-down-arrow');
        });

        // Simply reassign positions sequentially starting from 1
        optionContainers.forEach((container, index) => {
            const posField = container.querySelector('.child-position');
            if (posField) {
                posField.value = index + 1;
            }
        });
    }

    setPopoverContent() {
        var popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'));
        popoverTriggerList.map(function (popoverTriggerEl) {
            var triggerStyle = popoverTriggerEl.getAttribute('data-trigger-style')
            var popoverStyle = { container: 'body' }
            if(triggerStyle !== 'click') {
                popoverStyle['trigger'] = 'hover'
            }
            return new bootstrap.Popover(popoverTriggerEl, popoverStyle);
        });
    }

    observeDOMChanges() {
        // Create a MutationObserver to watch for new questions being added
        const observer = new MutationObserver((mutations) => {
            let shouldUpdate = false;

            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        // Check if a new nested-fields element was added
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            if (node.classList && node.classList.contains('nested-fields')) {
                                shouldUpdate = true;
                            } else if (node.querySelector && node.querySelector('.nested-fields')) {
                                shouldUpdate = true;
                            }
                        }
                    });
                }
            });

            if (shouldUpdate) {
                // Wait a bit for the DOM to settle, then update positions
                setTimeout(() => {
                    this.setNewQuestionPosition();
                    this.setNewOptionPosition();
                    this.evaluateNewPositions();
                    this.initializeNewGroupedContainers();
                    this.setPopoverContent();
                }, 100);
            }
        });

        // Start observing
        observer.observe(this.wrapper, {
            childList: true,
            subtree: true
        });
    }

    initializeNewGroupedContainers() {
        // Find any new grouped question containers that haven't been initialized yet
        // Use attribute selector to find containers with IDs containing 'grouped-question-container'
        const groupedContainers = document.querySelectorAll('[id*="grouped-question-container"]');
        groupedContainers.forEach(container => {
            if (!container.hasAttribute('data-questionnaire-initialized')) {
                container.setAttribute('data-questionnaire-initialized', 'true');
                new Questionnaire(container);
            }
        });

        // Find any new option containers that haven't been initialized yet
        const optionContainers = document.querySelectorAll('#option-container');
        optionContainers.forEach(container => {
            if (!container.hasAttribute('data-questionnaire-initialized')) {
                container.setAttribute('data-questionnaire-initialized', 'true');
                new Questionnaire(container);
            }
        });
    }

    // Initialize "Mark All as Required" functionality for grouped questions
    initializeGroupedQuestionRequiredCheckboxes() {
        // Function to update "Mark All as Required" checkbox based on individual required checkboxes
        const updateMarkAllCheckbox = ($groupContainer) => {
            const $markAllCheckbox = $groupContainer.closest('.nested-fields').find('.grouped-question-mark-all-required');
            const $requiredCheckboxes = $groupContainer.find('.nested-fields input[type="checkbox"][name*="[required]"]');

            if ($requiredCheckboxes.length === 0) {
                $markAllCheckbox.prop('checked', false);
                return;
            }

            const allChecked = $requiredCheckboxes.length > 0 && $requiredCheckboxes.filter(':checked').length === $requiredCheckboxes.length;
            $markAllCheckbox.prop('checked', allChecked);
        };

        // Handle "Mark All as Required" checkbox change
        $(document).off('change', '.grouped-question-mark-all-required').on('change', '.grouped-question-mark-all-required', function() {
            const $checkbox = $(this);
            const isChecked = $checkbox.is(':checked');
            const $groupContainer = $checkbox.closest('.nested-fields').find('[id^="grouped-question-container"]');

            // Find all required checkboxes within this group's sub-questions (excluding the parent checkbox)
            $groupContainer.find('.nested-fields input[type="checkbox"][name*="[required]"]').each(function() {
                $(this).prop('checked', isChecked);
            });
        });

        // Handle individual required checkbox changes
        $(document).off('change', '[id^="grouped-question-container"] .nested-fields input[type="checkbox"][name*="[required]"]').on('change', '[id^="grouped-question-container"] .nested-fields input[type="checkbox"][name*="[required]"]', function() {
            const $groupContainer = $(this).closest('[id^="grouped-question-container"]');
            updateMarkAllCheckbox($groupContainer);
        });

        // Initialize "Mark All as Required" checkbox state on page load
        $('.grouped-question-mark-all-required').each(function() {
            const $groupContainer = $(this).closest('.nested-fields').find('[id^="grouped-question-container"]');
            updateMarkAllCheckbox($groupContainer);
        });

        // Observe DOM changes to update checkbox when questions are added/removed
        const observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach(function(node) {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const $addedNode = $(node);
                            // Check if a nested-fields was added or removed
                            if ($addedNode.hasClass('nested-fields') || $addedNode.find('.nested-fields').length > 0) {
                                const $groupContainer = $addedNode.closest('[id^="grouped-question-container"]');
                                if ($groupContainer.length > 0) {
                                    setTimeout(function() {
                                        updateMarkAllCheckbox($groupContainer);
                                    }, 100);
                                }
                            }
                        }
                    });

                    mutation.removedNodes.forEach(function(node) {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const $removedNode = $(node);
                            if ($removedNode.hasClass('nested-fields') || $removedNode.find('.nested-fields').length > 0) {
                                // Find all grouped containers and update their checkboxes
                                $('[id^="grouped-question-container"]').each(function() {
                                    const $groupContainer = $(this);
                                    setTimeout(function() {
                                        updateMarkAllCheckbox($groupContainer);
                                    }, 100);
                                });
                            }
                        }
                    });
                }
            });
        });

        // Observe all grouped question containers
        $('[id^="grouped-question-container"]').each(function() {
            observer.observe(this, {
                childList: true,
                subtree: true
            });
        });

        // Also observe when new containers are added dynamically
        const containerObserver = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                mutation.addedNodes.forEach(function(node) {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const $node = $(node);
                        const $containers = $node.find('[id^="grouped-question-container"]').add($node.filter('[id^="grouped-question-container"]'));
                        $containers.each(function() {
                            observer.observe(this, {
                                childList: true,
                                subtree: true
                            });
                        });
                    }
                });
            });
        });

        // Observe the main container for new grouped question containers
        const $mainContainer = $('#question-container, [id^="grouped-question-container"]').closest('form, body');
        if ($mainContainer.length > 0) {
            containerObserver.observe($mainContainer[0], {
                childList: true,
                subtree: true
            });
        }
    }

    // Effects Account Expiry always forces Requires Approval to be checked too
    // (an expired profile must always be resubmitted for city review). The
    // Question model enforces this regardless of what gets submitted — this
    // just keeps the checkbox UI in sync live and blocks casual mouse/keyboard
    // attempts to uncheck it while Effects Account Expiry stays checked.
    initializeExpiryApprovalSync() {
        const sync = ($affectsExpiry) => {
            const $container = $affectsExpiry.closest('.applicant-question-options');
            const $requiresReapproval = $container.find('input[type="checkbox"][name$="[requires_reapproval]"]');
            if ($requiresReapproval.length === 0) return;

            const locked = $affectsExpiry.is(':checked');
            if (locked) {
                $requiresReapproval.prop('checked', true);
            }
            $requiresReapproval.css({
                'pointer-events': locked ? 'none' : '',
                opacity: locked ? '0.6' : ''
            });
            $requiresReapproval.attr('title', locked ? 'Required because Effects Account Expiry is checked' : '');
        };

        // Effects Account Expiry toggled — sync its sibling Requires Approval checkbox
        $(document).off('change.expiryApprovalSync', 'input[type="checkbox"][name$="[affects_expiry]"]')
            .on('change.expiryApprovalSync', 'input[type="checkbox"][name$="[affects_expiry]"]', function () {
                sync($(this));
            });

        // Guard against unchecking Requires Approval (e.g. via keyboard) while
        // its sibling Effects Account Expiry remains checked
        $(document).off('change.expiryApprovalSync', 'input[type="checkbox"][name$="[requires_reapproval]"]')
            .on('change.expiryApprovalSync', 'input[type="checkbox"][name$="[requires_reapproval]"]', function () {
                const $checkbox = $(this);
                const $container = $checkbox.closest('.applicant-question-options');
                const $affectsExpiry = $container.find('input[type="checkbox"][name$="[affects_expiry]"]');
                if ($affectsExpiry.length && $affectsExpiry.is(':checked') && !$checkbox.is(':checked')) {
                    $checkbox.prop('checked', true);
                }
            });

        // Apply current state on load for already-checked date questions
        $('input[type="checkbox"][name$="[affects_expiry]"]').each(function () {
            sync($(this));
        });
    }

    // Helper method to check if a nested field is a sub-question within a grouped question
    isSubQuestionInGroupedQuestion(nestedField) {
        // Check if this nested field is inside a grouped question container
        const groupedQuestionContainer = nestedField.closest('#grouped-question-container');
        if (groupedQuestionContainer) {
            return true;
        }

        // Also check if it's inside any element with a grouped question container ID pattern
        const parentWithGroupedContainer = nestedField.closest('[id*="grouped-question-container"]');
        if (parentWithGroupedContainer) {
            return true;
        }

        return false;
    }
}

// Initialize when document is ready
document.addEventListener('turbolinks:load', () => {
    // Initialize main question container
    // Note: do NOT guard with data-questionnaire-initialized here — Turbolinks cache
    // restores the attribute but drops all event listeners, so we always re-initialize.
    const wrapper = document.querySelector('#question-container');
    if (wrapper) {
        wrapper.setAttribute('data-questionnaire-initialized', 'true');
        const questionnaireInstance = new Questionnaire(wrapper);

        // Override the default behavior of cocoon for the grouped question as it is causing issues(conflict with the ID)
        // .off() first, matching every other binding in this file — without it, a
        // Turbolinks cache-restore that preserves this element re-stacks the handler.
        $("#grouped-question-link").off("click.groupedQuestionLink").on("click.groupedQuestionLink", function (e) {
            e.preventDefault();
            e.stopPropagation();

            const id = new Date().getTime();

            const newTemplate = this.dataset.associationInsertionTemplate
                .replace(/grouped-question-container/g, `grouped-question-container-${id}`)
                .replace(/\[new_questions\]/g, new Date().getTime());
            const $node = $(newTemplate);
            $("#question-container").append($node.hide().fadeIn());

            // Close the Bootstrap dropdown (safely)
            const dropdownToggle = $(this).closest('.dropdown').find('#actionMenuLink')[0];
            const dropdown = bootstrap.Dropdown.getInstance(dropdownToggle);
            if (dropdown) dropdown.hide();
        });

        // Initialize grouped question containers
        const groupedContainers = document.querySelectorAll('[id*="grouped-question-container"]');
        groupedContainers.forEach(container => {
            container.setAttribute('data-questionnaire-initialized', 'true');
            new Questionnaire(container);
        });

        // Initialize option containers
        const optionContainers = document.querySelectorAll('#option-container');
        optionContainers.forEach(container => {
            container.setAttribute('data-questionnaire-initialized', 'true');
            new Questionnaire(container);
        });

        // Initialize "Mark All as Required" functionality for grouped questions
        questionnaireInstance.initializeGroupedQuestionRequiredCheckboxes();

        // Effects Account Expiry forces Requires Approval to be checked too
        questionnaireInstance.initializeExpiryApprovalSync();
    }
});
